import { injectable, inject } from 'inversify';
import JsonRpcDispatcher, { interceptors } from '@dealroadshow/json-rpc-dispatcher';
import DITypes from './DI/types';
import type { IAdapterFactory, IDispatcherFactory, IDispatcher } from './DI/interfaces';
import Container from '@framework/di/Container';
import dmPortalUrl from '@dm-portal/url';
import addSessionData from '@users/session/interceptors/rpc/addSessionData';
import checkSession from '@users/session/interceptors/rpc/checkSession';
import userPermissionsErrorHandler from '@condor/json-rpc-dispatcher/interceptors/rpc/userPermissionsErrorHandler';
import parseWrappedError from '@framework/api/Rpc/parseWrappedError';
import OTelInstrument from '@framework/otel/OTelInstrument';

@injectable()
export class DispatcherFactory implements IDispatcherFactory {
  constructor(
    @inject(Container) private readonly container: Container,
    @inject(DITypes.AdapterFactory) private readonly adapterFactory: IAdapterFactory,
  ) {}

  create(): IDispatcher {
    const adapter = this.adapterFactory.create(
      dmPortalUrl.getRpcUrl(),
      { otelPropagation: true },
    );

    const dispatcher = new JsonRpcDispatcher(adapter);

    dispatcher
      .interceptRequest(addSessionData(this.container))
      .interceptResponse(checkSession(this.container))
      .interceptResponse(userPermissionsErrorHandler(this.container))
      .interceptResponse(parseWrappedError())
      .interceptResponse(interceptors.response.transformErrorToException);

    OTelInstrument<typeof dispatcher.call>(
      dispatcher,
      'call',
      this.container,
      (request) => `rpc.call: ${ request.method }`,
      (request) => {
        return {
          attributes: {
            'rpc.method': request.method,
          },
        };
      },
    );

    return dispatcher;
  }
}
